import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, inject, Injectable } from '@angular/core';

import { Logger } from '../logger/services/logger.service';

@Injectable({ providedIn: 'root' })
export class ExceptionService implements ErrorHandler {
  private readonly logger = inject(Logger);

  private loggedErrors = new Set<string>();

  public handleError(err: any): void {
    const actual = this.unwrap(err);
    const signature = this.getErrorSignature(actual);

    if (this.loggedErrors.has(signature)) {
      return;
    }
    this.loggedErrors.add(signature);

    if (actual instanceof HttpErrorResponse) {
      if (actual.status === 0) {
        this.logger.error(
          'HttpOffline',
          actual,
          `Backend unreachable (${actual.url})`,
          this.extractStack(actual)
        );
        return;
      }
      this.logger.error(
        'HttpError',
        actual,
        `HTTP ${actual.status} ${actual.url}`,
        this.extractStack(actual)
      );
      return;
    }

    if (actual && actual.reason) {
      const r = actual.reason;
      const stack = (r && r.stack) || this.extractStack(r);
      const msg = (r && r.message) || this.extractMessage(r);
      this.logger.error('UnhandledPromiseRejection', r, msg, stack);
      return;
    }

    const stack = this.extractStack(actual);
    const message = this.extractMessage(actual);
    this.logger.error('UnhandledException', actual, message, stack);
  }

  private unwrap(e: any): any {
    if (e && e.rejection) {
      return e.rejection;
    }
    return e;
  }

  private extractMessage(e: any): string {
    if (!e) { return 'Unknown error'; }

    if (e.message) { return e.message; }
    return typeof e === 'string' ? e : e.toString();
  }

  private extractStack(e: any): string {
    if (e && e.error && e.error.stack) { return e.error.stack; }

    if (e?.stack) { return e.stack; }
    return '';
  }

  private getErrorSignature(err: any): string {
    if (!err) { return 'unknown'; }
    const message = this.extractMessage(err);
    const stack = this.extractStack(err);
    return `${err.constructor?.name ?? 'Unknown'}:${message}:${stack}`;
  }
}
